"use client";

import { useTranslations } from "next-intl";

import { championLabel, formatMetricValue } from "@/entities/replay-metrics";
import { ChampionIcon } from "@/shared/ui/data-display/champion-icon";
import { cn } from "@/shared/lib/utils";
import { RoleIcon } from "@/shared/ui/data-display/role-icon";

/**
 * KAN-814: пары по ролям в разборе игры (хвост KAN-763).
 *
 * Отдельный компонент рядом с `MatchGameTeamStats`, а НЕ внутри него: источники
 * разные — здесь пары ролей из нарратива матча, там строки состава, — и склейка в
 * один файл связала бы правки двух эпиков.
 *
 * НАПРАВЛЕНИЕ, А НЕ ПОБЕДА. Бэкенд отдаёт `leadingSide` — у кого величина больше, —
 * и витрина подписывает это словом «впереди», а не «лучше». Больше фарма у лесника
 * не делает его сильнее оппонента: у ролей разные задачи, и превращать сравнение
 * чисел в вердикт значило бы придумать смысл, которого в данных нет.
 *
 * Разницу считает бэкенд. Здесь её не пересчитываем — на фронте всплыло бы
 * вычитание из отсутствующего значения.
 */

/** Порядок строк — привычный порядок линий. */
const ROLE_ORDER = ["TOP", "JUNGLE", "MIDDLE", "BOTTOM", "UTILITY"];

export interface LaneStat {
  key: string;
  blueValue?: number | null;
  redValue?: number | null;
  /** "100" — синие, "200" — красные, null — равенство или сравнить не удалось. */
  leadingSide?: string | null;
  delta?: number | null;
}

export interface LanePlayer {
  userId?: string | null;
  name?: string | null;
  champion?: string | null;
  win?: boolean;
}

export interface LaneMatchup {
  position: string;
  blue?: LanePlayer | null;
  red?: LanePlayer | null;
  stats?: LaneStat[] | null;
}

/**
 * Формат зависит от величины, и это не косметика.
 *
 * Ровно над этой витриной стоит табло игры, где золото и урон подписаны как «20.8k».
 * Показать здесь «20799» значит заставить читателя сверять две записи одного и того же
 * числа и гадать, одна ли это величина. Доля участия в убийствах приходит как 0.6 —
 * в виде «0.6» она читается как штука, а не как 60 %.
 */
function formatLaneValue(key: string, value: number): string {
  // ДОЛЯ форматируется общей функцией: доля остаётся долей независимо от того, за одну
  // игру она посчитана или за турнир, и вторая копия этого правила уже разъезжалась.
  if (key === "killParticipation") return formatMetricValue(key, value);

  // А вот СЧЁТ — нет. `turretPlates` в блоках метрик это среднее за игру, и «3.0»
  // там осмысленно: среднее бывает дробным. Здесь та же подпись означает другое —
  // сколько плит сбито в ЭТОЙ игре, и дробная часть у счёта выдумана. Делегировать
  // общей функции всё подряд по совпадению ключа значило бы считать, что одинаковое
  // имя гарантирует одинаковую величину; на стенде это дало «3.0 плиты за игру».

  // Золото и урон — тысячи; та же запись, что в табло игры выше.
  if (key === "gold" || key === "damageToChampions") {
    return value >= 1000
      ? `${(value / 1000).toFixed(1)}k`
      : Math.round(value).toString();
  }

  if (Number.isInteger(value)) return String(value);
  return value >= 100 ? Math.round(value).toString() : value.toFixed(1);
}

function PlayerSide({ player, align }: { player?: LanePlayer | null; align: "left" | "right" }) {
  const t = useTranslations("Bracket.laneMatchups");
  const name = (player?.name ?? "").trim() || t("unknownPlayer");

  return (
    <span
      className={cn(
        "flex min-w-0 items-center gap-2",
        align === "right" && "flex-row-reverse text-right",
      )}
    >
      {player?.champion ? (
        <span className="relative block h-6 w-6 shrink-0 overflow-hidden rounded">
          <ChampionIcon
            name={player.champion}
          label={championLabel(player.champion)}
            size={24}
            className="size-full scale-[1.15] object-cover"
            title={championLabel(player.champion)}
          />
        </span>
      ) : null}
      <span className="min-w-0 truncate text-[13px] font-medium">{name}</span>
    </span>
  );
}

export function MatchLaneMatchups({ matchups }: { matchups: LaneMatchup[] | null | undefined }) {
  const t = useTranslations("Bracket.laneMatchups");
  const axis = useTranslations("Profile.behaviorMetrics.axis");
  const lane = useTranslations("Bracket.laneMatchups.stat");

  const rows = (matchups ?? []).filter((m) => m.blue && m.red);
  if (rows.length === 0) return null;

  const sorted = [...rows].sort((a, b) => {
    const ai = ROLE_ORDER.indexOf((a.position ?? "").toUpperCase());
    const bi = ROLE_ORDER.indexOf((b.position ?? "").toUpperCase());
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  /**
   * Подпись величины. `turretPlates` — поведенческая ось (KAN-759), её подпись берём
   * из словаря метрик, чтобы не заводить вторую для одной величины.
   *
   * `killParticipation` осью НЕ является, и попытка взять подпись оттуда выдавала на
   * стенде сырой ключ посреди русских подписей: отсутствующий перевод не падает, он
   * тихо печатает путь. Поэтому у витрины есть собственная подпись, а тест ниже держит
   * список ключей бэкенда и их подписей вместе.
   */
  const statLabel = (key: string): string => {
    if (key === "turretPlates") return axis(key);
    return lane(key);
  };

  return (
    <section className="rounded-[13px] border border-surface-inset-border bg-surface-inset p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          {t("title")}
        </span>
        {/* Оговорка обязательна: без неё стрелка читается как «сыграл лучше». */}
        <span className="text-[11px] text-muted-foreground">{t("subtitle")}</span>
      </div>

      <div className="flex flex-col gap-3">
        {sorted.map((matchup) => {
          const stats = (matchup.stats ?? []).filter(
            (s) => s.blueValue != null || s.redValue != null,
          );

          return (
            <div
              key={matchup.position}
              className="rounded-[10px] border border-border bg-card p-3"
            >
              <div className="mb-2 grid grid-cols-[minmax(0,1fr)_28px_minmax(0,1fr)] items-center gap-2">
                <PlayerSide player={matchup.blue} align="left" />
                <span className="flex items-center justify-center text-muted-foreground">
                  <RoleIcon role={matchup.position} size={18} />
                </span>
                <PlayerSide player={matchup.red} align="right" />
              </div>

              {stats.length > 0 ? (
                <div className="flex flex-col gap-1">
                  {stats.map((stat) => {
                    const blueLeads = stat.leadingSide === "100";
                    const redLeads = stat.leadingSide === "200";

                    return (
                      <div
                        key={stat.key}
                        className="grid grid-cols-[52px_minmax(0,1fr)_52px] items-center gap-2 text-[12px] tabular-nums"
                      >
                        <span
                          className={cn(
                            "text-right",
                            blueLeads ? "font-semibold text-foreground" : "text-muted-foreground",
                          )}
                        >
                          {stat.blueValue == null ? "—" : formatLaneValue(stat.key, stat.blueValue)}
                        </span>

                        <span className="flex items-center justify-center gap-1.5 text-center text-[11px] text-muted-foreground">
                          {statLabel(stat.key)}
                          {/* Разница показывается только когда есть кого с кем
                              сравнить: односторонняя величина разницы не имеет. */}
                          {stat.delta != null && stat.leadingSide ? (
                            <span className="text-[10px] text-muted-foreground/70">
                              +{formatLaneValue(stat.key, stat.delta)}
                            </span>
                          ) : null}
                        </span>

                        <span
                          className={cn(
                            "text-left",
                            redLeads ? "font-semibold text-foreground" : "text-muted-foreground",
                          )}
                        >
                          {stat.redValue == null ? "—" : formatLaneValue(stat.key, stat.redValue)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[12px] text-muted-foreground">{t("noStats")}</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
